import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';

/**
 * Converts various date formats to a Date object
 * Handles:
 * - Date objects
 * - ISO date strings
 * - Firestore Timestamps
 * - Unix timestamps (numbers)
 * - Objects with seconds/nanoseconds (Firestore timestamp structure)
 * 
 * @param dateValue - The date value to convert
 * @returns Date object or null if invalid
 */
export function toDate(dateValue: any): Date | null {
  if (!dateValue) return null;

  // Already a Date object
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // Firestore Timestamp
  if (dateValue instanceof Timestamp) {
    return dateValue.toDate();
  }

  // Object with toDate method (Firestore Timestamp-like)
  if (dateValue && typeof dateValue.toDate === 'function') {
    try {
      const date = dateValue.toDate();
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  // Object with seconds and nanoseconds (Firestore timestamp structure)
  if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
    try {
      // Create timestamp from seconds and nanoseconds
      const timestamp = new Timestamp(dateValue.seconds, dateValue.nanoseconds || 0);
      return timestamp.toDate();
    } catch {
      return null;
    }
  }

  // Number (Unix timestamp in milliseconds)
  if (typeof dateValue === 'number') {
    // Ensure it's a reasonable timestamp (after year 2000)
    if (dateValue > 946684800000) {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }
    // If it's in seconds, convert to milliseconds
    else if (dateValue > 946684800) {
      const date = new Date(dateValue * 1000);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  // String (ISO date string or other parseable format)
  if (typeof dateValue === 'string') {
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Safely converts a date value to ISO string
 * Returns empty string if conversion fails
 * 
 * @param dateValue - The date value to convert
 * @returns ISO string or empty string
 */
export function toISOStringSafe(dateValue: any): string {
  const date = toDate(dateValue);
  return date ? date.toISOString() : '';
}

/**
 * Checks if a date value is valid
 * 
 * @param dateValue - The date value to check
 * @returns boolean indicating if the date is valid
 */
export function isValidDate(dateValue: any): boolean {
  const date = toDate(dateValue);
  return date !== null;
}

/**
 * Formats a date value to a localized string
 * Returns empty string if conversion fails
 * 
 * @param dateValue - The date value to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string or empty string
 */
export function formatDate(dateValue: any, options?: Intl.DateTimeFormatOptions): string {
  const date = toDate(dateValue);
  return date ? date.toLocaleDateString(undefined, options) : '';
}

/**
 * Formats a date value to a localized date and time string
 * Returns empty string if conversion fails
 * 
 * @param dateValue - The date value to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date/time string or empty string
 */
export function formatDateTime(dateValue: any, options?: Intl.DateTimeFormatOptions): string {
  const date = toDate(dateValue);
  return date ? date.toLocaleString(undefined, options) : '';
}

/**
 * Safely formats a date using date-fns format function
 * Returns a default value if the date is invalid
 * 
 * @param dateValue - The date value to format
 * @param formatString - The date-fns format string
 * @param defaultValue - The default value to return if date is invalid (default: 'N/A')
 * @returns Formatted date string or default value
 */
export function formatDateSafe(dateValue: any, formatString: string, defaultValue: string = 'N/A'): string {
  try {
    const date = toDate(dateValue);
    if (!date) {
      console.warn('Invalid date value passed to formatDateSafe:', dateValue);
      return defaultValue;
    }
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, 'Date value:', dateValue);
    return defaultValue;
  }
}